import {
    AlertCircle,
    AlignJustify,
    ArrowLeft,
    Boxes,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Grid3X3,
    ImageIcon,
    LayoutGrid,
    ListOrdered,
    Lock,
    Minus,
    Package,
    PanelLeft,
    Pencil,
    Plus,
    Save,
    Search,
    ShoppingBag,
    Trash2,
    Users,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getCustomers } from '../../../api/customer.api';
import { getPackages } from '../../../api/package.api';
import { getCategoriesByType, getServices, searchServices } from '../../../api/service.api';
import styles from '../../../assets/scss/pages/CreateOrder.module.scss';
import pkgStyles from '../../../assets/scss/pages/PackageSell.module.scss';
import api from '../../../config/axios-conf';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/authStore';
import useCartStore, {
    buildCheckoutPayload,
    generateIdempotencyKey,
    resolvePackagePrice,
    toSessionEditItemDto,
    validatePackageCart,
    validateSessionCart,
} from '../../../stores/cartStore';
import useServiceTabsStore, { selectVisibleTabKeys } from '../../../stores/serviceTabsStore';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';
import KeypadInput from '../../../components/common/KeypadInput';
import PackageItemEditModal from '../packages/PackageItemEditModal';
import PackageSellerPickerModal from '../packages/PackageSellerPickerModal';
import SessionPerformerPickerModal from '../packages/SessionPerformerPickerModal';

const DEFAULT_IMG = 'https://sotiem.vn/uploads/default-service.png';
const SNAPSHOT_CUTOVER_TS = new Date('2026-03-21T01:27:00+07:00').getTime();
const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

function getAvatarColor(name) {
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function normalize(str) {
    return (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, '')
        .toLowerCase();
}

function getItemPrice(variant) {
    return (
        (variant?.salePrice && Number(variant.salePrice) > 1000
            ? Number(variant.salePrice)
            : Number(variant?.retailPrice)) || 0
    );
}

function getPriceDisplay(variants, formatFn) {
    if (!variants || variants.length === 0) return formatFn(0);
    let min = Infinity,
        max = 0;
    variants.forEach((v) => {
        const p = getItemPrice(v);
        if (p < min) min = p;
        if (p > max) max = p;
    });
    if (min === max) return formatFn(min);
    return `${formatFn(min)} - ${formatFn(max)}`;
}

function getVariantName(variant) {
    return variant?.attributeValues?.map((v) => v.value).join(' - ') || '';
}

export default function CreateOrder() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const editBillId = searchParams.get('edit');
    const user = useAuthStore((s) => s.user);
    const preselectedCustomer = location.state?.preselectedCustomer || null;

    const { hasPermission } = usePermissions();
    const canOverridePackage = hasPermission(PERMISSIONS.PACKAGE.SELL_OVERRIDE);

    // Cart store
    const {
        addToCart,
        removeFromCart,
        updateQuantity,
        removeAllFromCart,
        replaceCart,
        getCart,
        getTotal,
        setSupportEmployees,
        setSupportEmployeesForAll,
        // ===== Gói dịch vụ (bán gói) =====
        addPackageItem,
        removeOnePackage,
        updatePackageQuantity,
        removeAllPackageFromCart,
        getPackageCart,
        getPackageTotal,
        // ===== Dùng buổi gói (session) =====
        addSessionItem,
        removeSessionItem,
        removeAllSessionItems,
        replaceSessionCart,
        getSessionCart,
    } = useCartStore();

    // Customer
    const [customers, setCustomers] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');

    // Services catalog
    const [allServices, setAllServices] = useState([]);
    const [catalogTab, setCatalogTab] = useState('all');

    // Tab catalog: 'Tất cả' luôn đầu, các tab type lấy thứ tự/ẩn-hiện từ serviceTabsStore
    const serviceTabs = useServiceTabsStore((s) => s.tabs);
    const CATALOG_TAB_LABELS = {
        dichvu: 'Dịch vụ',
        sanpham: 'Sản phẩm',
        combo: 'Combo',
        lieutrinh: 'Gói dịch vụ',
    };
    const catalogTabs = useMemo(() => {
        const ordered = selectVisibleTabKeys(serviceTabs, ['dichvu', 'sanpham', 'combo', 'lieutrinh']).map(
            (key) => ({ key, label: CATALOG_TAB_LABELS[key] }),
        );
        return [{ key: 'all', label: 'Tất cả' }, ...ordered];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceTabs]);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [searchResults, setSearchResults] = useState(null); // null = no search, [] = API results
    const [loadingServices, setLoadingServices] = useState(true);
    const [categories, setCategories] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categoryLayout, setCategoryLayout] = useState(
        () => localStorage.getItem('orderCategoryLayout') || 'sidebar',
    );
    const [serviceLayout, setServiceLayout] = useState(() => localStorage.getItem('orderServiceLayout') || 'grid');

    const handleCategoryLayoutChange = (layout) => {
        setCategoryLayout(layout);
        localStorage.setItem('orderCategoryLayout', layout);
    };
    const handleServiceLayoutChange = (layout) => {
        setServiceLayout(layout);
        localStorage.setItem('orderServiceLayout', layout);
    };

    // Order form
    const [discountType, setDiscountType] = useState('%');
    const [discountValue, setDiscountValue] = useState('');
    const [pointUsed, setPointUsed] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('Tiền mặt');
    const [status, setStatus] = useState('paid');
    const [prepayment, setPrepayment] = useState('');
    const [note, setNote] = useState('');
    const [internalNote, setInternalNote] = useState('');

    // Point rules
    const [earnRule, setEarnRule] = useState(null);
    const [redeemValuePerPoint, setRedeemValuePerPoint] = useState(200);

    // Variant inline expand
    const [expandedServiceId, setExpandedServiceId] = useState(null);

    // Server-side pagination
    const PAGE_SIZE = 30;
    const [svcPage, setSvcPage] = useState(1);
    const [svcHasMore, setSvcHasMore] = useState(true);
    const [svcTotal, setSvcTotal] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerRef = useRef(null);

    // Employee modal
    const [employees, setEmployees] = useState([]);
    const [empModal, setEmpModal] = useState(null);
    const [empSelected, setEmpSelected] = useState({});
    const [empSearch, setEmpSearch] = useState('');

    // Gói dịch vụ (bán gói): catalog + modal override/chọn NV bán
    const isPackageTab = catalogTab === 'lieutrinh';
    const [packagesCatalog, setPackagesCatalog] = useState([]);
    const [loadingPackages, setLoadingPackages] = useState(false);
    const [sellerPickerUid, setSellerPickerUid] = useState(null);
    const [editingPackageUid, setEditingPackageUid] = useState(null);
    const checkoutIdempotencyKeyRef = useRef(null); // giữ key để retry an toàn khi /bills/checkout fail

    // Dùng buổi gói (session): thẻ active của khách + chọn NV thực hiện từng buổi
    const [activePackages, setActivePackages] = useState([]);
    const [loadingActivePackages, setLoadingActivePackages] = useState(false);
    const [showUsePackageSection, setShowUsePackageSection] = useState(false);
    const [sessionPerformerPicker, setSessionPerformerPicker] = useState(null); // { uid, sessionIdx }

    // UI
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [toastMsg, setToastMsg] = useState(null);
    const [isLoadingBill, setIsLoadingBill] = useState(!!editBillId);
    // Sửa đơn có gói/buổi (port app): gói đã có trong bill + snapshot ban đầu để build payload PUT
    const [existingPackages, setExistingPackages] = useState([]); // gói đã bán trong bill khi edit
    const [originalBillData, setOriginalBillData] = useState(null); // { packageIds, packageUsageIds }

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const customerId = customer?.id;
    const cartItems = customerId ? getCart(customerId) : [];
    // Giỏ gói mới + giỏ buổi — cho phép cả khi TẠO MỚI lẫn SỬA đơn (port app).
    const packageCart = customerId ? getPackageCart(customerId) : [];
    const sessionCart = customerId ? getSessionCart(customerId) : [];
    const serviceAmount = customerId ? getTotal(customerId) : 0;
    const packageTotal = customerId ? getPackageTotal(customerId) : 0;
    // Tổng giá trị gói đã có trong bill (chỉ khi edit bill có gói) — cộng vào tổng như app.
    const existingPackagesTotal = existingPackages.reduce(
        (sum, p) => sum + Number(p.totalContractValue || 0),
        0,
    );
    const amount = serviceAmount + packageTotal + existingPackagesTotal; // dùng buổi = 0đ
    const isGuest = customer?.name === 'Khách lẻ';

    // Chặn đổi khách khi sửa đơn có gói/buổi (port app cannotChangeCustomerInEdit) — reactive.
    const editBillLocked =
        !!editBillId &&
        (existingPackages.length > 0 || (originalBillData?.packageUsageIds?.length || 0) > 0);

    // Tên NV (để hiển thị thợ thực hiện đã chọn) — lấy từ danh sách employees đã load
    const performerName = (id) => employees.find((e) => String(e.id) === String(id))?.name;

    // Id gói đã có trong bill ban đầu nhưng user vừa "Xoá khỏi đơn" (port app).
    const removedFromBillPackageIds = useMemo(() => {
        if (!editBillId || !originalBillData) return new Set();
        const originalIds = new Set(originalBillData.packageIds || []);
        const currentIds = new Set(existingPackages.map((p) => p.id));
        const removed = new Set();
        originalIds.forEach((id) => {
            if (!currentIds.has(id)) removed.add(id);
        });
        return removed;
    }, [editBillId, originalBillData, existingPackages]);

    // Khi gói bị gỡ khỏi đơn → tự xoá session cart entry tương ứng (tránh PUT vừa xoá gói vừa dùng buổi).
    useEffect(() => {
        if (!customerId || removedFromBillPackageIds.size === 0) return;
        const cart = getSessionCart(customerId);
        cart.forEach((s) => {
            if (removedFromBillPackageIds.has(Number(s.customerPackageId))) {
                removeSessionItem(customerId, s._uid);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removedFromBillPackageIds, customerId]);

    // Gói active có thể dùng buổi (ẩn gói vừa bị gỡ khỏi đơn) — port app.
    const usableActivePackages = activePackages.filter((pkg) => !removedFromBillPackageIds.has(pkg.id));

    // Có hiển thị mục "Dùng gói dịch vụ" không (khách có thẻ active dùng được, không phải khách lẻ) — cả khi edit.
    const hasUseSection =
        !!customer && !isGuest && (loadingActivePackages || usableActivePackages.length > 0);

    // === Discount & Point calculation ===
    const discountAmount =
        discountType === '%' ? (amount * (parseFloat(discountValue) || 0)) / 100 : parseFloat(discountValue) || 0;
    const payByPoint = pointUsed * redeemValuePerPoint;
    const totalDiscount = discountAmount + payByPoint;
    const pay = Math.max(0, amount - totalDiscount);
    // Số điểm tối đa có thể dùng (không vượt quá số tiền cần trả sau giảm giá)
    const maxPointUsable = Math.floor((amount - discountAmount) / redeemValuePerPoint);

    // Points earned
    let pointsEarned = 0;
    if (earnRule && !isGuest && status === 'paid' && pay >= (earnRule.minOrderValue || 0)) {
        if (earnRule.earnMode === 'by_bill') {
            pointsEarned = Math.floor(earnRule.rewardValue || 0);
        } else {
            pointsEarned = Math.floor(pay / (earnRule.pointsRequired || 1)) * (earnRule.rewardValue || 0);
        }
    }

    // === Fetch services page ===
    const fetchServicesPage = useCallback(async (page, tab, catId, reset = false) => {
        if (page > 1) setLoadingMore(true);
        try {
            const params = { limit: PAGE_SIZE, page };
            if (tab !== 'all') params.typeService = tab;
            if (catId && catId !== 'all') params.categoryId = catId;
            const { data } = await getServices(params);
            const list = (data.services || []).filter((s) => !s.isDeleted);
            const pagination = data.pagination || {};
            setAllServices((prev) => {
                if (reset) return list;
                // Deduplicate khi append (server có thể trả duplicate do join)
                const existingIds = new Set(prev.map((s) => s.id));
                return [...prev, ...list.filter((s) => !existingIds.has(s.id))];
            });
            setSvcHasMore(page < (pagination.totalPages || 1));
            setSvcTotal(pagination.total || 0);
            setSvcPage(page);
        } catch (err) {
            console.error('Fetch services error:', err);
        } finally {
            setLoadingMore(false);
        }
    }, []);

    // === Fetch init (customers, categories, rules) + first page services ===
    useEffect(() => {
        const fetchInit = async () => {
            try {
                const [custRes, catDvRes, catSpRes, catCbRes, rulesRes] = await Promise.all([
                    getCustomers({ limit: 10000, page: 1 }),
                    getCategoriesByType('dichvu'),
                    getCategoriesByType('sanpham'),
                    getCategoriesByType('combo'),
                    api.get('/points/active-rules'),
                ]);
                const custList = custRes.data?.data || custRes.data || [];
                custList.sort((a, b) => (a.name === 'Khách lẻ' ? -1 : b.name === 'Khách lẻ' ? 1 : 0));
                setCustomers(custList);
                if (!editBillId) {
                    if (preselectedCustomer) {
                        const found = custList.find((c) => c.id === preselectedCustomer.id) || preselectedCustomer;
                        setCustomer(found);
                    } else {
                        const guest = custList.find((c) => c.name === 'Khách lẻ');
                        if (guest) setCustomer(guest);
                    }
                }

                setCategories({
                    dichvu: catDvRes.data?.serviceCategory || [],
                    sanpham: catSpRes.data?.serviceCategory || [],
                    combo: catCbRes.data?.serviceCategory || [],
                });

                const rules = rulesRes.data;
                if (rules?.earnRule) setEarnRule(rules.earnRule);
                if (rules?.redeemRule?.rewardValue) setRedeemValuePerPoint(Number(rules.redeemRule.rewardValue));

                api.get('/employees/supportEmployeeAll')
                    .then((res) => setEmployees(res.data?.employees || []))
                    .catch(() => {});

                // First page of services
                await fetchServicesPage(1, 'all', 'all', true);
            } catch (err) {
                console.error('Init error:', err);
            } finally {
                setLoadingServices(false);
            }
        };
        fetchInit();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // === Tab / Category change → reset services and fetch page 1 ===
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (catalogTab === 'lieutrinh') return; // gói dịch vụ fetch riêng (endpoint khác)
        setSvcPage(1);
        setSvcHasMore(true);
        fetchServicesPage(1, catalogTab, selectedCategory, true);
    }, [catalogTab, selectedCategory, fetchServicesPage]);

    // === Fetch gói dịch vụ khi vào tab 'lieutrinh' (load toàn bộ, giống ServiceList/app) ===
    useEffect(() => {
        if (catalogTab !== 'lieutrinh' || packagesCatalog.length > 0) return;
        let alive = true;
        setLoadingPackages(true);
        getPackages()
            .then((res) => {
                if (!alive) return;
                const list = (res?.data?.data || res?.data || []).filter((p) => !p.isDeleted);
                setPackagesCatalog(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (alive) setPackagesCatalog([]);
            })
            .finally(() => {
                if (alive) setLoadingPackages(false);
            });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogTab]);

    // === Fetch thẻ gói đang hoạt động của khách (để dùng buổi) — port app, cả khi edit ===
    useEffect(() => {
        if (!customerId || isGuest) {
            setActivePackages([]);
            return;
        }
        let cancelled = false;
        setLoadingActivePackages(true);
        api.get(`/customer-packages?customerId=${customerId}&status=active`)
            .then((res) => {
                if (cancelled) return;
                const list = res?.data?.data || res?.data || [];
                const pkgs = Array.isArray(list) ? list : [];
                setActivePackages(pkgs);
                if (pkgs.length > 0) setShowUsePackageSection(true);
            })
            .catch(() => {
                if (!cancelled) setActivePackages([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingActivePackages(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, isGuest, editBillId]);

    // === Load more (called by sentinel) ===
    const loadMore = useCallback(() => {
        if (loadingMore || !svcHasMore) return;
        fetchServicesPage(svcPage + 1, catalogTab, selectedCategory, false);
    }, [loadingMore, svcHasMore, svcPage, catalogTab, selectedCategory, fetchServicesPage]);

    // === Load bill for editing ===
    useEffect(() => {
        if (!editBillId) return;
        const loadBill = async () => {
            setIsLoadingBill(true);
            try {
                const { data } = await api.get(`/bills/${editBillId}`);
                const bill = data.bill || data;

                // Set customer trực tiếp từ bill
                if (bill.customer) setCustomer(bill.customer);

                // Payment method — chuẩn hoá nhiều biến thể trong DB
                const pmLower = (bill.paymentMethod || '').toLowerCase().trim();
                setPaymentMethod(
                    pmLower === 'chuyển khoản' ||
                        pmLower === 'chuyen khoan' ||
                        pmLower === 'chuyenkhoan' ||
                        pmLower === 'chuyen khoản'
                        ? 'Chuyển khoản'
                        : 'Tiền mặt',
                );

                setNote(bill.note || '');
                setInternalNote(bill.internalNote || '');
                setStatus(bill.status || 'paid');
                setPointUsed(Number(bill.pointUsed || 0));

                // Prepayment = bill.total (số tiền đã thu)
                const billTotal = Number(bill.total);
                setPrepayment(isNaN(billTotal) ? '' : String(billTotal));

                // Discount — dùng SNAPSHOT_CUTOVER_TS để tính pointCash đúng
                const billDiscountType = bill.discountType || 'VND';
                setDiscountType(billDiscountType);
                const checkoutTs = bill.checkoutTime ? new Date(bill.checkoutTime).getTime() : 0;
                const shouldUseSnapshot =
                    checkoutTs >= SNAPSHOT_CUTOVER_TS && bill.pointCash !== null && bill.pointCash !== undefined;
                const payByPointValue = shouldUseSnapshot
                    ? Number(bill.pointCash || 0)
                    : Number(bill.pointUsed || 0) * 200;
                const discountNum = parseFloat(bill.discount) || 0;
                const pureDiscount = Math.max(0, discountNum - payByPointValue);

                let discountVal = '';
                if (billDiscountType === '%') {
                    const origTotal = parseFloat(bill.originalTotal) || 0;
                    if (origTotal > 0 && pureDiscount > 0) {
                        const pct = Math.round((pureDiscount / origTotal) * 1000) / 10; // 1 chữ số thập phân
                        discountVal = String(pct);
                    }
                } else {
                    discountVal = pureDiscount > 0 ? String(Math.round(pureDiscount)) : '';
                }
                setDiscountValue(discountVal);

                // Rebuild cart — giống app: group theo serviceId+variantId,
                // tính lại quantity và unitPrice từ detailsCount / numEmployees
                const groups = {};
                (bill.details || []).forEach((d) => {
                    const key = `${d.serviceId}-${d.variant?.id ?? 'null'}`;
                    if (!groups[key]) {
                        groups[key] = {
                            serviceId: d.serviceId,
                            name: d.service?.name || 'Dịch vụ',
                            urlImg: d.service?.urlImg || '',
                            variant: d.variant || null,
                            variantName: d.variant?.attributeValues?.map((a) => a.value).join(' - ') || null,
                            employees: new Set(),
                            detailsCount: 0,
                            totalDetailPrice: 0,
                        };
                    }
                    groups[key].employees.add(d.employeeId);
                    const qty = d.quantity != null ? parseFloat(d.quantity) : 1;
                    const price = d.price != null ? parseFloat(d.price) : 0;
                    if (!isNaN(qty) && !isNaN(price) && qty > 0) {
                        groups[key].detailsCount += qty;
                        groups[key].totalDetailPrice += price * qty;
                    }
                });

                const items = Object.values(groups).map((g) => {
                    const empIds = Array.from(g.employees);
                    const perUnit = Math.max(1, empIds.length);
                    const detailsCount = parseFloat(g.detailsCount) || 0;
                    const totalDetailPrice = parseFloat(g.totalDetailPrice) || 0;
                    const quantityUnits = Math.max(0.1, parseFloat((detailsCount / perUnit).toFixed(10)));
                    const unitPrice = quantityUnits > 0 ? Math.floor(totalDetailPrice / quantityUnits) : 0;
                    return {
                        id: g.serviceId,
                        name: g.name,
                        urlImg: g.urlImg,
                        selectedVariant: g.variant ? { ...g.variant } : null,
                        variantName: g.variantName,
                        price: unitPrice,
                        quantity: quantityUnits,
                        supportEmployees: empIds.map((eid) => ({ employeeId: eid })),
                    };
                });

                const cid = bill.customer?.id;
                if (cid) replaceCart(cid, items);

                // ── Gói/buổi đã có trong bill (port app prefill) ──
                const pkgs = (bill.customerPackages || []).filter((p) => !p.isDeleted);
                setExistingPackages(pkgs);

                // Prefill session cart từ packageUsages (gộp theo customerPackageId)
                const packageUsages = Array.isArray(bill.packageUsages) ? bill.packageUsages : [];
                let originalSessionItems = [];
                if (cid && packageUsages.length > 0) {
                    const grouped = new Map();
                    [...packageUsages]
                        .sort((a, b) => (Number(a.sessionNumber) || 0) - (Number(b.sessionNumber) || 0))
                        .forEach((u) => {
                            const cpId = Number(u.customerPackageId);
                            if (!cpId) return;
                            if (!grouped.has(cpId)) {
                                const cp = u.customerPackage || {};
                                grouped.set(cpId, {
                                    customerPackageId: cpId,
                                    code: cp.code || '',
                                    packageName: cp.templateSnapshot?.name || '',
                                    amountPerSession: Number(cp.amountPerSession || 0),
                                    remainingSessions: 0,
                                    sessionCount: 0,
                                    performers: [],
                                });
                            }
                            const entry = grouped.get(cpId);
                            entry.sessionCount += 1;
                            entry.performers.push(u.employeeId != null ? Number(u.employeeId) : null);
                        });
                    originalSessionItems = Array.from(grouped.values());
                    replaceSessionCart(cid, originalSessionItems);
                } else if (cid) {
                    replaceSessionCart(cid, []);
                }

                // Snapshot ban đầu để build payload PUT (biết đơn gốc có gói/buổi)
                setOriginalBillData({
                    packageIds: pkgs.map((p) => p.id),
                    packageUsageIds: packageUsages.map((u) => u.id),
                });
            } catch (err) {
                console.error('Load bill error:', err);
                setError('Không thể tải hoá đơn');
            } finally {
                setIsLoadingBill(false);
            }
        };
        loadBill();
    }, [editBillId]); // eslint-disable-line react-hooks/exhaustive-deps

    // === Customer selection ===
    const handleSelectCustomer = (cust) => {
        if (customer?.id && customer.id !== cust.id) {
            useCartStore.getState().transferCartBetweenCustomers(customer.id, cust.id);
            // Thẻ/buổi gắn với khách cũ → không chuyển sang khách mới (port app)
            removeAllSessionItems(customer.id);
        }
        setCustomer(cust);
        if (cust.name === 'Khách lẻ') setPointUsed(0);
        setShowCustomerModal(false);
        setCustomerSearch('');
    };

    const filteredCustomers = customers.filter((c) => {
        if (!customerSearch.trim()) return true;
        const q = normalize(customerSearch);
        return normalize(c.name).includes(q) || normalize(c.phone).includes(q);
    });

    // === Add service to cart ===
    const handleAddService = (service) => {
        if (!customerId) return;
        // Ưu tiên full data từ allServices (có variants), fallback về search result
        const fullService = allServices.find((s) => s.id === service.id) || service;
        const variants = fullService.variants || [];
        if (variants.length === 0) {
            // Combo hoặc dịch vụ không có biến thể — dùng giá trực tiếp
            addToCart(customerId, {
                id: fullService.id,
                name: fullService.name,
                urlImg: fullService.urlImg || '',
                price: Math.floor(fullService.price || 0),
                selectedVariant: null,
                variantName: null,
            });
            return;
        }
        if (variants.length === 1) {
            const v = variants[0];
            addToCart(customerId, {
                id: fullService.id,
                name: fullService.name,
                urlImg: fullService.urlImg || '',
                price: getItemPrice(v),
                selectedVariant: v,
                variantName: null,
            });
        } else {
            // Toggle expand: click lần 1 mở, click lần 2 tắt
            setExpandedServiceId((prev) => (prev === fullService.id ? null : fullService.id));
        }
    };

    // === Thêm gói vào giỏ bán (port mapping app addPackageItem) ===
    const handleAddPackage = (pkg) => {
        if (!customerId) {
            showToast('error', 'Vui lòng chọn khách hàng trước');
            return;
        }
        addPackageItem(customerId, {
            packageId: pkg.id,
            name: pkg.name,
            urlImg: pkg.urlImg || null,
            code: pkg.code || null,
            templatePrice: pkg.price,
            templateSalePrice:
                Number(pkg.salePrice) > 0 && Number(pkg.salePrice) < Number(pkg.price)
                    ? pkg.salePrice
                    : null,
            templateSessions: pkg.totalSessions,
            templateValidityMonths: pkg.validityMonths,
            sellerCommissionPercent: pkg.sellerCommissionPercent,
            performerCommissionPercent: pkg.performerCommissionPercent,
        });
    };

    const filteredPackages = packagesCatalog.filter((p) => {
        if (!catalogSearch.trim()) return true;
        const q = catalogSearch.toLowerCase();
        return (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q);
    });

    const handleSelectVariant = (service, variant) => {
        if (!customerId) return;
        addToCart(customerId, {
            id: service.id,
            name: service.name,
            urlImg: service.urlImg || '',
            price: getItemPrice(variant),
            selectedVariant: variant,
            variantName: getVariantName(variant),
        });
        setExpandedServiceId(null);
    };

    // === Catalog filter ===
    // Debounce search → call API, lưu kết quả trực tiếp
    useEffect(() => {
        const q = catalogSearch.trim();
        if (!q) {
            setSearchResults(null);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await searchServices(q);
                setSearchResults(data.services || []);
            } catch {
                setSearchResults([]);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [catalogSearch]);

    // Ref callback cho sentinel — khi mount, tạo observer gọi loadMore
    const sentinelRef = useCallback(
        (node) => {
            if (observerRef.current) observerRef.current.disconnect();
            if (!node) return;
            observerRef.current = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) loadMore();
                },
                { threshold: 0.1 },
            );
            observerRef.current.observe(node);
        },
        [loadMore],
    );

    const currentCategories = catalogTab === 'all' ? [] : categories[catalogTab] || [];

    // Server đã filter theo tab + category → chỉ cần dùng allServices trực tiếp
    const filteredServices = allServices;

    const isSearching = searchResults !== null;
    const displayServices = isSearching ? searchResults : filteredServices;

    // === Submit ===
    const handleSubmit = async () => {
        if (isSubmitting) return;
        setError('');

        if (!customerId) {
            setError('Vui lòng chọn khách hàng');
            return;
        }
        if (
            cartItems.length === 0 &&
            packageCart.length === 0 &&
            sessionCart.length === 0 &&
            existingPackages.length === 0
        ) {
            setError('Vui lòng thêm ít nhất 1 dịch vụ hoặc gói');
            return;
        }
        // Khách lẻ không mua/dùng gói (port app guard: gồm cả gói đã có khi edit)
        if (
            isGuest &&
            (packageCart.length > 0 || sessionCart.length > 0 || existingPackages.length > 0)
        ) {
            setError('Khách lẻ không dùng được gói dịch vụ. Vui lòng chọn khách hàng.');
            return;
        }

        // Validate points
        if (pointUsed > 0 && !isGuest) {
            try {
                const { data } = await api.get(`/customers/${customerId}`);
                const latestPoint = data.customer?.point || data.point || 0;
                if (latestPoint < pointUsed) {
                    setError(`Khách chỉ có ${latestPoint} điểm, không đủ ${pointUsed} điểm`);
                    return;
                }
            } catch {
                /* ignore */
            }
        }

        setIsSubmitting(true);
        try {
            // Build details
            const details = [];
            cartItems.forEach((item) => {
                const empList = item.supportEmployees || [];
                if (empList.length > 0) {
                    empList.forEach((emp) => {
                        details.push({
                            serviceId: item.id,
                            variantId: item.selectedVariant?.id || null,
                            quantity: item.quantity / empList.length,
                            employeeId: emp.employeeId,
                            price: Math.floor(item.price / empList.length),
                        });
                    });
                } else {
                    details.push({
                        serviceId: item.id,
                        variantId: item.selectedVariant?.id || null,
                        quantity: item.quantity,
                        employeeId: user?.id || user?.userId,
                        price: Math.floor(item.price),
                    });
                }
            });

            let computedTotal, computedOwe;
            const prepay = parseFloat(prepayment) || 0;
            if (status === 'partial') {
                computedTotal = prepay;
                computedOwe = Math.max(pay - prepay, 0);
            } else if (status === 'paid') {
                computedTotal = pay;
                computedOwe = 0;
            } else {
                computedTotal = 0;
                computedOwe = status === 'pending' ? pay : 0;
            }

            const billData = {
                customerId,
                employeeId: user?.id || user?.userId,
                originalTotal: Math.round(amount),
                total: computedTotal,
                discount: discountAmount + payByPoint,
                discountType,
                pointUsed: isGuest ? 0 : pointUsed,
                pointReceived: 0,
                paymentMethod,
                note,
                internalNote,
                status,
                owe: computedOwe,
                details,
            };

            // ─── BRANCH: hoá đơn mới có gói / dùng buổi → /bills/checkout (port app handlePay) ───
            if (!editBillId && (packageCart.length > 0 || sessionCart.length > 0)) {
                const pkgErrors = packageCart.length > 0 ? validatePackageCart(packageCart) : [];
                const sessErrors = sessionCart.length > 0 ? validateSessionCart(sessionCart) : [];
                const allErrors = [...pkgErrors, ...sessErrors];
                if (allErrors.length) {
                    setError(allErrors.join('\n'));
                    setIsSubmitting(false);
                    return;
                }
                if (!checkoutIdempotencyKeyRef.current) {
                    checkoutIdempotencyKeyRef.current = generateIdempotencyKey();
                }
                const payload = buildCheckoutPayload({ ...billData, paymentMethod }, packageCart, sessionCart);
                const res = await api.post('/bills/checkout', payload, {
                    headers: { 'Idempotency-Key': checkoutIdempotencyKeyRef.current },
                });
                const checkoutBillId = res.data?.data?.bill?.id || res.data?.bill?.id || res.data?.id;
                checkoutIdempotencyKeyRef.current = null;
                if (preselectedCustomer?.id) {
                    api.delete(`/customers/checkin/${preselectedCustomer.id}`).catch(() => {});
                }
                removeAllFromCart(customerId);
                removeAllPackageFromCart(customerId);
                removeAllSessionItems(customerId);
                showToast('success', 'Tạo hoá đơn thành công');
                setTimeout(() => {
                    if (status === 'paid' && checkoutBillId) {
                        navigate(`/admin/bills/${checkoutBillId}`, {
                            state: preselectedCustomer ? { fromWaiting: true } : undefined,
                        });
                    } else {
                        navigate('/admin/dashboard');
                    }
                }, 1200);
                return;
            }

            // ─── BRANCH: sửa hoá đơn → PUT /bills/:id (port app, gói-aware) ───
            if (editBillId) {
                const hasNewPackages = packageCart.length > 0;
                const hasSessions = sessionCart.length > 0;
                const hasExistingPackages = existingPackages.length > 0;

                if (hasNewPackages) {
                    const e = validatePackageCart(packageCart);
                    if (e.length) {
                        setError(e.join('\n'));
                        setIsSubmitting(false);
                        return;
                    }
                }
                if (hasSessions) {
                    const e = validateSessionCart(sessionCart);
                    if (e.length) {
                        setError(e.join('\n'));
                        setIsSubmitting(false);
                        return;
                    }
                }

                // Gói/buổi chỉ gửi khi đơn thật sự có (gồm cả trường hợp đơn gốc có mà user xoá hết)
                const originallyHadPackages = (originalBillData?.packageIds || []).length > 0;
                const originallyHadSessions = (originalBillData?.packageUsageIds || []).length > 0;
                const shouldHandlePackages = hasExistingPackages || hasNewPackages || originallyHadPackages;
                const shouldHandleSessions = hasSessions || originallyHadSessions;

                const newPackageItems = packageCart.flatMap((p) =>
                    Array.from({ length: Number(p.quantity) || 1 }, () => ({
                        packageId: p.packageId,
                        sellerEmployeeId: p.sellerEmployeeId ?? undefined,
                        ...(p.customPrice != null && { customPrice: Number(p.customPrice) }),
                        ...(p.customSessions != null && { customSessions: Number(p.customSessions) }),
                        ...(p.customValidityMonths != null && {
                            customValidityMonths: Number(p.customValidityMonths),
                        }),
                        ...(p.overrideReason && { overrideReason: p.overrideReason }),
                        ...(p.note && { note: p.note }),
                        ...(p.internalNote && { internalNote: p.internalNote }),
                    })),
                );

                // Đơn thuần dịch vụ (không cờ nào bật) → updatePayload === billData → PUT y hệt logic cũ.
                const updatePayload = {
                    ...billData,
                    ...(shouldHandlePackages && {
                        keepPackageIds: existingPackages.map((p) => p.id),
                        newPackageItems,
                    }),
                    ...(shouldHandleSessions && {
                        sessionItems: sessionCart.map(toSessionEditItemDto),
                    }),
                };

                await api.put(`/bills/${editBillId}`, updatePayload);
                removeAllFromCart(customerId);
                if (hasNewPackages) removeAllPackageFromCart(customerId);
                if (hasSessions) removeAllSessionItems(customerId);
                setExistingPackages([]);
                showToast('success', 'Cập nhật hoá đơn thành công');
                setTimeout(() => {
                    navigate(`/admin/bills/${editBillId}`, { replace: true, state: { fromEdit: true } });
                }, 1200);
                return;
            }

            // ─── BRANCH: tạo mới hoá đơn dịch vụ thuần → /bills/create (giữ nguyên) ───
            const res = await api.post('/bills/create', billData);
            const newBillId = res.data?.bill?.id || res.data?.id;
            if (preselectedCustomer?.id) {
                api.delete(`/customers/checkin/${preselectedCustomer.id}`).catch(() => {});
            }
            showToast('success', 'Tạo hoá đơn thành công');
            removeAllFromCart(customerId);
            setTimeout(() => {
                if (status === 'paid' && newBillId) {
                    navigate(`/admin/bills/${newBillId}`, {
                        state: preselectedCustomer ? { fromWaiting: true } : undefined,
                    });
                } else {
                    navigate('/admin/dashboard');
                }
            }, 1200);
        } catch (err) {
            setError(err.response?.data?.message || 'Tạo hoá đơn thất bại');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoadingBill || loadingServices) {
        return (
            <div className={styles.page}>
                {/* Header skeleton */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.skeletonRect} style={{ width: 40, height: 40 }} />
                        <div className={styles.skeletonLine} style={{ width: 140 }} />
                    </div>
                    <div className={styles.skeletonRect} style={{ width: 120, height: 42 }} />
                </div>

                <div className={styles.layout}>
                    {/* Left: catalog skeleton */}
                    <div className={styles.layoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeletonLine} style={{ width: 180 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {/* Search bar */}
                                <div className={styles.skeletonRect} style={{ height: 46, marginBottom: 14 }} />
                                {/* Toolbar */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {[80, 72, 80, 70].map((w, i) => (
                                            <div
                                                key={i}
                                                className={styles.skeletonRect}
                                                style={{ width: w, height: 36 }}
                                            />
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {[38, 38, 8, 38, 38, 38].map((w, i) => (
                                            <div
                                                key={i}
                                                className={styles.skeletonRect}
                                                style={{ width: w, height: 38 }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {/* Service grid */}
                                <div className={styles.serviceGrid}>
                                    {Array.from({ length: 10 }).map((_, i) => (
                                        <div key={i} className={styles.skeletonRect} style={{ height: 200 }} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Middle: cart skeleton */}
                    <div className={styles.layoutMiddle}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeletonLine} style={{ width: 100 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: 'flex',
                                            gap: 12,
                                            padding: '12px 0',
                                            borderBottom: '1px solid #F3F4F6',
                                        }}
                                    >
                                        <div
                                            className={styles.skeletonRect}
                                            style={{ width: 44, height: 44, flexShrink: 0 }}
                                        />
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <div className={styles.skeletonLine} style={{ width: '70%' }} />
                                            <div className={styles.skeletonLine} style={{ width: '40%', height: 11 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: customer + payment skeleton */}
                    <div className={styles.layoutRight}>
                        <div className={styles.customerBar} style={{ cursor: 'default', marginBottom: 16 }}>
                            <div className={styles.skeletonCircle} style={{ width: 44, height: 44, flexShrink: 0 }} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div className={styles.skeletonLine} style={{ width: '60%' }} />
                                <div className={styles.skeletonLine} style={{ width: '40%', height: 11 }} />
                            </div>
                        </div>
                        <div className={styles.card}>
                            <div className={styles.cardBody}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} style={{ marginBottom: 16 }}>
                                        <div className={styles.skeletonLine} style={{ width: 80, marginBottom: 8 }} />
                                        <div className={styles.skeletonRect} style={{ height: 40 }} />
                                    </div>
                                ))}
                                <div className={styles.skeletonRect} style={{ height: 1, margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <div className={styles.skeletonLine} style={{ width: 80 }} />
                                    <div className={styles.skeletonLine} style={{ width: 100 }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {error && <div className={styles.errorMsg}>{error}</div>}

            <div className={styles.layout}>
                {/* ===== LEFT: Service catalog (grid) ===== */}
                <div className={styles.layoutLeft}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader} style={{ background: '#0098f6', color: '#fff' }}>
                            <button className={styles.backBtnInline} onClick={() => navigate(-1)}>
                                <ArrowLeft size={18} />
                            </button>
                            Chọn dịch vụ / sản phẩm
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.catalogSearch}>
                                <Search size={16} />
                                <input
                                    value={catalogSearch}
                                    onChange={(e) => setCatalogSearch(e.target.value)}
                                    placeholder="Tìm kiếm: tên, mã SP, mã SKU..."
                                />
                                {catalogSearch && (
                                    <button
                                        style={{
                                            border: 'none',
                                            background: 'none',
                                            cursor: 'pointer',
                                            color: '#6B7280',
                                        }}
                                        onClick={() => setCatalogSearch('')}
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            {/* Toolbar: type tabs + layout toggles */}
                            <div className={styles.catalogToolbar}>
                                <div className={styles.catalogTabs} style={{ marginBottom: 0 }}>
                                    {catalogTabs.map((t) => (
                                        <button
                                            key={t.key}
                                            className={`${styles.catalogTab}${catalogTab === t.key ? ` ${styles.catalogTabActive}` : ''}`}
                                            onClick={() => {
                                                setCatalogTab(t.key);
                                                setSelectedCategory('all');
                                            }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.layoutToggles}>
                                    {/* Category layout group */}
                                    {[
                                        { key: 'chips', icon: <AlignJustify size={16} />, title: 'Danh mục dạng chip' },
                                        { key: 'sidebar', icon: <PanelLeft size={16} />, title: 'Danh mục sidebar' },
                                    ].map((l) => (
                                        <button
                                            key={l.key}
                                            title={l.title}
                                            className={`${styles.layoutToggleBtn}${categoryLayout === l.key ? ` ${styles.layoutToggleBtnActive}` : ''}`}
                                            onClick={() => handleCategoryLayoutChange(l.key)}
                                        >
                                            {l.icon}
                                        </button>
                                    ))}
                                    <div className={styles.layoutToggleDivider} />
                                    {/* Service layout group */}
                                    {[
                                        { key: 'grid', icon: <LayoutGrid size={16} />, title: 'Lưới lớn' },
                                        { key: 'compact', icon: <Grid3X3 size={16} />, title: 'Lưới nhỏ' },
                                        {
                                            key: 'listDetail',
                                            icon: <ListOrdered size={16} />,
                                            title: 'Danh sách chi tiết',
                                        },
                                    ].map((l) => (
                                        <button
                                            key={l.key}
                                            title={l.title}
                                            className={`${styles.layoutToggleBtn}${serviceLayout === l.key ? ` ${styles.layoutToggleBtnActive}` : ''}`}
                                            onClick={() => handleServiceLayoutChange(l.key)}
                                        >
                                            {l.icon}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Category chips (chỉ hiện khi chips mode + đang ở tab cụ thể) */}
                            {categoryLayout === 'chips' && catalogTab !== 'all' && currentCategories.length > 0 && (
                                <div className={styles.catalogTabs} style={{ marginBottom: 12 }}>
                                    <button
                                        className={`${styles.catalogTab}${selectedCategory === 'all' ? ` ${styles.catalogTabActive}` : ''}`}
                                        onClick={() => setSelectedCategory('all')}
                                    >
                                        Tất cả
                                    </button>
                                    {currentCategories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            className={`${styles.catalogTab}${selectedCategory === cat.id ? ` ${styles.catalogTabActive}` : ''}`}
                                            onClick={() => setSelectedCategory(cat.id)}
                                        >
                                            {cat.name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Service content */}
                            {isPackageTab ? (
                                loadingPackages ? (
                                    <div className={styles.catalogEmpty}>Đang tải gói dịch vụ...</div>
                                ) : filteredPackages.length === 0 ? (
                                    <div className={styles.catalogEmpty}>Chưa có gói dịch vụ</div>
                                ) : (
                                    <div className={styles.gridWrapper}>
                                        <div
                                            className={styles.serviceGrid}
                                            style={{
                                                gridTemplateColumns:
                                                    serviceLayout === 'gridSmall'
                                                        ? 'repeat(auto-fill, minmax(120px, 1fr))'
                                                        : 'repeat(auto-fill, minmax(150px, 1fr))',
                                            }}
                                        >
                                            {filteredPackages.map((pkg) => {
                                                const hasSale =
                                                    Number(pkg.salePrice) > 0 &&
                                                    Number(pkg.salePrice) < Number(pkg.price);
                                                return (
                                                    <div key={pkg.id} className={styles.serviceCardWrap}>
                                                        <div
                                                            className={styles.serviceCard}
                                                            onClick={() => handleAddPackage(pkg)}
                                                        >
                                                            <div className={styles.serviceCardImgWrap}>
                                                                {pkg.urlImg ? (
                                                                    <img
                                                                        src={pkg.urlImg}
                                                                        alt=""
                                                                        className={styles.serviceCardImg}
                                                                        onError={(e) => {
                                                                            if (!e.target.dataset.fallback) {
                                                                                e.target.dataset.fallback = '1';
                                                                                e.target.src = DEFAULT_IMG;
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className={styles.serviceCardImgPlaceholder}
                                                                    >
                                                                        <Boxes size={28} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className={styles.serviceCardBody}>
                                                                <div className={styles.serviceCardName}>
                                                                    {pkg.name}
                                                                </div>
                                                                <div className={styles.serviceCardPrice}>
                                                                    {hasSale && (
                                                                        <span className={pkgStyles.pkgPriceOld}>
                                                                            {formatCurrency(pkg.price)}
                                                                        </span>
                                                                    )}
                                                                    {formatCurrency(hasSale ? pkg.salePrice : pkg.price)}
                                                                </div>
                                                                <div className={styles.serviceCardVariantCount}>
                                                                    {Number(pkg.totalSessions || 0)} buổi
                                                                    {pkg.validityMonths
                                                                        ? ` · HSD ${pkg.validityMonths} tháng`
                                                                        : ''}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            ) : loadingServices ? (
                                <div className={styles.catalogEmpty}>Đang tải...</div>
                            ) : displayServices.length === 0 ? (
                                <div className={styles.catalogEmpty}>Không tìm thấy</div>
                            ) : (
                                <div
                                    className={
                                        categoryLayout === 'sidebar' &&
                                        catalogTab !== 'all' &&
                                        currentCategories.length > 0
                                            ? styles.contentWithSidebar
                                            : undefined
                                    }
                                >
                                    {/* Category sidebar */}
                                    {categoryLayout === 'sidebar' &&
                                        catalogTab !== 'all' &&
                                        currentCategories.length > 0 && (
                                            <div className={styles.categorySidebar}>
                                                <div className={styles.sidebarTitle}>Danh mục</div>
                                                <button
                                                    className={`${styles.sidebarItem}${selectedCategory === 'all' ? ` ${styles.sidebarItemActive}` : ''}`}
                                                    onClick={() => setSelectedCategory('all')}
                                                >
                                                    <span>Tất cả</span>
                                                    <span className={styles.sidebarCount}>
                                                        {svcTotal}
                                                    </span>
                                                </button>
                                                {currentCategories.map((cat) => (
                                                    <button
                                                        key={cat.id}
                                                        className={`${styles.sidebarItem}${selectedCategory === cat.id ? ` ${styles.sidebarItemActive}` : ''}`}
                                                        onClick={() => setSelectedCategory(cat.id)}
                                                    >
                                                        <span>{cat.name}</span>
                                                        <span className={styles.sidebarCount}>
                                                            {(cat.groupLinks || []).length}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                    {/* Service grid/list */}
                                    <div className={styles.gridWrapper}>
                                        {serviceLayout === 'listDetail' ? (
                                            <div className={styles.catalogList}>
                                                {displayServices.map((svc) => {
                                                    const variants = svc.variants || [];
                                                    const priceDisplay =
                                                        variants.length > 0
                                                            ? getPriceDisplay(variants, formatCurrency)
                                                            : formatCurrency(svc.price || 0);
                                                    const isExpanded = expandedServiceId === svc.id;
                                                    return (
                                                        <div key={svc.id}>
                                                            <div
                                                                className={styles.catalogItem}
                                                                onClick={() => handleAddService(svc)}
                                                            >
                                                                {svc.urlImg ? (
                                                                    <img
                                                                        src={svc.urlImg}
                                                                        alt=""
                                                                        className={styles.catalogItemImg}
                                                                        onError={(e) => {
                                                                            if (!e.target.dataset.fallback) {
                                                                                e.target.dataset.fallback = '1';
                                                                                e.target.src = DEFAULT_IMG;
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className={styles.catalogItemImgPlaceholder}>
                                                                        <ImageIcon size={28} />
                                                                    </div>
                                                                )}
                                                                <div className={styles.catalogItemInfo}>
                                                                    <div className={styles.catalogItemName}>
                                                                        {svc.name}
                                                                    </div>
                                                                    {svc.code && (
                                                                        <div
                                                                            style={{
                                                                                fontSize: 13,
                                                                                color: '#6B7280',
                                                                                marginTop: 2,
                                                                            }}
                                                                        >
                                                                            {svc.code}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 15,
                                                                            fontWeight: 700,
                                                                            color: '#0098F6',
                                                                        }}
                                                                    >
                                                                        {priceDisplay}
                                                                    </div>
                                                                    {variants.length > 1 && (
                                                                        <div
                                                                            style={{
                                                                                fontSize: 12,
                                                                                color: '#6B7280',
                                                                                marginTop: 2,
                                                                            }}
                                                                        >
                                                                            {variants.length} loại
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <button className={styles.catalogItemAdd}>
                                                                    <Plus size={18} />
                                                                </button>
                                                            </div>
                                                            {isExpanded && variants.length > 1 && (
                                                                <div
                                                                    className={styles.variantDropdown}
                                                                    ref={(el) => {
                                                                        if (!el) return;
                                                                        const rect =
                                                                            el.parentElement.getBoundingClientRect();
                                                                        el.style.top = `${rect.top}px`;
                                                                        el.style.left = `${rect.right + 4}px`;
                                                                    }}
                                                                >
                                                                    {variants.map((v) => (
                                                                        <div
                                                                            key={v.id}
                                                                            className={styles.variantDropdownItem}
                                                                            onClick={() => handleSelectVariant(svc, v)}
                                                                        >
                                                                            <span
                                                                                className={styles.variantDropdownName}
                                                                            >
                                                                                {getVariantName(v) || 'Mặc định'}
                                                                            </span>
                                                                            <span
                                                                                className={styles.variantDropdownPrice}
                                                                            >
                                                                                {formatCurrency(getItemPrice(v))}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {!isSearching && svcHasMore && (
                                                    <div ref={sentinelRef} className={styles.loadMoreHint}>
                                                        Đang tải thêm...
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* GRID / COMPACT */
                                            <div
                                                className={styles.serviceGrid}
                                                style={
                                                    serviceLayout === 'compact'
                                                        ? {
                                                              gridTemplateColumns:
                                                                  'repeat(auto-fill, minmax(150px, 1fr))',
                                                              gap: 10,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {displayServices.map((svc) => {
                                                    const variants = svc.variants || [];
                                                    const priceDisplay =
                                                        variants.length > 0
                                                            ? getPriceDisplay(variants, formatCurrency)
                                                            : formatCurrency(svc.price || 0);
                                                    const isExpanded = expandedServiceId === svc.id;
                                                    return (
                                                        <div key={svc.id} className={styles.serviceCardWrap}>
                                                            <div
                                                                className={`${styles.serviceCard}${isExpanded ? ` ${styles.serviceCardExpanded}` : ''}`}
                                                                onClick={() => handleAddService(svc)}
                                                            >
                                                                <div className={styles.serviceCardImgWrap}>
                                                                    {svc.urlImg ? (
                                                                        <img
                                                                            src={svc.urlImg}
                                                                            alt=""
                                                                            className={styles.serviceCardImg}
                                                                            onError={(e) => {
                                                                                if (!e.target.dataset.fallback) {
                                                                                    e.target.dataset.fallback = '1';
                                                                                    e.target.src = DEFAULT_IMG;
                                                                                }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className={styles.serviceCardImgPlaceholder}
                                                                        >
                                                                            <ImageIcon size={36} />
                                                                        </div>
                                                                    )}
                                                                    {variants.some(
                                                                        (v) =>
                                                                            v.salePrice && Number(v.salePrice) > 1000,
                                                                    ) && <span className={styles.saleBadge}>Sale</span>}
                                                                </div>
                                                                <div className={styles.serviceCardBody}>
                                                                    <div className={styles.serviceCardName}>
                                                                        {svc.name}
                                                                    </div>
                                                                    <div className={styles.serviceCardPrice}>
                                                                        {priceDisplay}
                                                                    </div>
                                                                    {svc.code && (
                                                                        <span
                                                                            className={styles.serviceCardVariantCount}
                                                                        >
                                                                            {svc.code}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {isExpanded && variants.length > 1 && (
                                                                <div
                                                                    className={styles.variantDropdown}
                                                                    ref={(el) => {
                                                                        if (!el) return;
                                                                        const rect =
                                                                            el.parentElement.getBoundingClientRect();
                                                                        el.style.top = `${rect.top}px`;
                                                                        el.style.left = `${rect.right + 4}px`;
                                                                    }}
                                                                >
                                                                    {variants.map((v) => (
                                                                        <div
                                                                            key={v.id}
                                                                            className={styles.variantDropdownItem}
                                                                            onClick={() => handleSelectVariant(svc, v)}
                                                                        >
                                                                            <span
                                                                                className={styles.variantDropdownName}
                                                                            >
                                                                                {getVariantName(v) || 'Mặc định'}
                                                                            </span>
                                                                            <span
                                                                                className={styles.variantDropdownPrice}
                                                                            >
                                                                                {formatCurrency(getItemPrice(v))}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {!isSearching && svcHasMore && (
                                                    <div ref={sentinelRef} className={styles.loadMoreHint}>
                                                        Đang tải thêm...
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ===== MIDDLE: Cart ===== */}
                <div className={styles.layoutMiddle}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span>
                                Giỏ hàng (
                                {cartItems.length +
                                    packageCart.length +
                                    sessionCart.length +
                                    existingPackages.length}
                                )
                            </span>
                            {(cartItems.length > 0 || packageCart.length > 0 || sessionCart.length > 0) && (
                                <button
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        color: '#EF4444',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!customerId) return;
                                        removeAllFromCart(customerId);
                                        removeAllPackageFromCart(customerId);
                                        removeAllSessionItems(customerId);
                                    }}
                                >
                                    Xoá tất cả
                                </button>
                            )}
                        </div>
                        <div className={styles.cardBody}>
                            {cartItems.length === 0 &&
                            packageCart.length === 0 &&
                            sessionCart.length === 0 &&
                            existingPackages.length === 0 &&
                            !hasUseSection ? (
                                <div className={styles.cartEmpty}>
                                    <ShoppingBag size={32} style={{ color: '#ECEEF2', marginBottom: 8 }} />
                                    <div>Chọn dịch vụ hoặc gói từ danh sách bên phải</div>
                                </div>
                            ) : (
                                <>
                                    {cartItems.map((item) => (
                                    <div
                                        key={item.productKey || `${item.id}-${item.selectedVariant?.id}`}
                                        className={styles.cartItem}
                                        style={{ cursor: 'pointer', position: 'relative' }}
                                        onClick={() => {
                                            const empMap = {};
                                            (item.supportEmployees || []).forEach((se) => {
                                                empMap[se.employeeId] = true;
                                            });
                                            setEmpSelected(empMap);
                                            setEmpModal(item);
                                            setEmpSearch('');
                                        }}
                                    >
                                        {item.urlImg ? (
                                            <img src={item.urlImg} alt="" className={styles.cartItemImg} />
                                        ) : (
                                            <div className={styles.cartItemImgPlaceholder}>
                                                <ImageIcon size={18} />
                                            </div>
                                        )}
                                        <div className={styles.cartItemInfo}>
                                            <div className={styles.cartItemName}>{item.name}</div>
                                            {item.variantName && (
                                                <div className={styles.cartItemVariant}>{item.variantName}</div>
                                            )}
                                            <div className={styles.cartItemPrice}>{formatCurrency(item.price)}</div>
                                        </div>
                                        <div className={styles.cartItemQty} onClick={(e) => e.stopPropagation()}>
                                            <button
                                                className={styles.qtyBtn}
                                                onClick={() =>
                                                    removeFromCart(customerId, item.id, item.selectedVariant?.id)
                                                }
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <KeypadInput
                                                className={styles.qtyInput}
                                                overlayClassName={styles.keypadRightOverlay}
                                                value={item.quantity}
                                                title="Số lượng"
                                                onChange={(raw) => {
                                                    const v = parseFloat(raw);
                                                    if (!isNaN(v) && v > 0)
                                                        updateQuantity(
                                                            customerId,
                                                            item.id,
                                                            item.selectedVariant?.id,
                                                            v,
                                                        );
                                                }}
                                            />
                                            <button
                                                className={styles.qtyBtn}
                                                onClick={() => addToCart(customerId, { ...item, quantity: 1 })}
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                        <span className={styles.cartItemTotal}>
                                            {formatCurrency(item.price * item.quantity)}
                                            <button
                                                className={styles.removeBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateQuantity(customerId, item.id, item.selectedVariant?.id, 0);
                                                }}
                                            >
                                                <Trash2 />
                                            </button>
                                        </span>
                                        {(item.supportEmployees || []).length > 0 ? (
                                            <span className={styles.empHasStaffBadge}>
                                                {item.supportEmployees.length} NV hỗ trợ
                                            </span>
                                        ) : (
                                            <span className={styles.empNoStaffBadge}>Chưa có NV</span>
                                        )}
                                    </div>
                                    ))}

                                    {/* ===== Giỏ gói dịch vụ (bán thẻ) ===== */}
                                    {packageCart.length > 0 && (
                                        <div className={pkgStyles.pkgCartSection}>
                                            {isGuest && (
                                                <div className={pkgStyles.pkgGuestWarn}>
                                                    <AlertCircle size={15} />
                                                    Khách lẻ không mua được gói. Vui lòng chọn khách hàng.
                                                </div>
                                            )}
                                            <div className={pkgStyles.pkgCartTitle}>Gói dịch vụ</div>
                                            {packageCart.map((pkg) => {
                                                const unitPrice = resolvePackagePrice(pkg);
                                                const priceOverridden =
                                                    pkg.customPrice != null &&
                                                    Number(pkg.customPrice) !== Number(pkg.templatePrice);
                                                const hasOverride =
                                                    pkg.customPrice != null ||
                                                    pkg.customSessions != null ||
                                                    pkg.customValidityMonths != null;
                                                return (
                                                    <div key={pkg._uid} className={pkgStyles.pkgRow}>
                                                        <div className={pkgStyles.pkgTop}>
                                                            {pkg.urlImg ? (
                                                                <img
                                                                    src={pkg.urlImg}
                                                                    alt=""
                                                                    className={pkgStyles.pkgImg}
                                                                />
                                                            ) : (
                                                                <div className={pkgStyles.pkgImgPlaceholder}>
                                                                    <Package size={20} />
                                                                </div>
                                                            )}
                                                            <div
                                                                className={pkgStyles.pkgInfo}
                                                                onClick={() => setSellerPickerUid(pkg._uid)}
                                                            >
                                                                <div className={pkgStyles.pkgName}>{pkg.name}</div>
                                                                <div className={pkgStyles.pkgPrice}>
                                                                    {priceOverridden && (
                                                                        <span className={pkgStyles.pkgPriceOld}>
                                                                            {formatCurrency(pkg.templatePrice)}
                                                                        </span>
                                                                    )}
                                                                    {formatCurrency(unitPrice)}
                                                                </div>
                                                            </div>
                                                            <button
                                                                className={pkgStyles.pkgRemoveBtn}
                                                                onClick={() =>
                                                                    updatePackageQuantity(customerId, pkg.packageId, 0)
                                                                }
                                                                title="Xoá khỏi giỏ"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>

                                                        <div className={pkgStyles.pkgBottom}>
                                                            <div
                                                                className={pkgStyles.pkgStatus}
                                                                style={{
                                                                    color: pkg.sellerEmployeeId
                                                                        ? '#22C55E'
                                                                        : '#F59E0B',
                                                                }}
                                                                onClick={() => setSellerPickerUid(pkg._uid)}
                                                            >
                                                                {pkg.sellerEmployeeId ? (
                                                                    <CheckCircle2 size={14} />
                                                                ) : (
                                                                    <AlertCircle size={14} />
                                                                )}
                                                                {pkg.sellerEmployeeId
                                                                    ? `${pkg.sellerName || 'NV ' + pkg.sellerEmployeeId}`
                                                                    : 'Chọn NV bán'}
                                                                {hasOverride ? ' · đã chỉnh' : ''}
                                                            </div>
                                                            <div className={pkgStyles.pkgActions}>
                                                                <div className={pkgStyles.pkgQtyBox}>
                                                                    <button
                                                                        className={pkgStyles.pkgQtyBtn}
                                                                        onClick={() =>
                                                                            removeOnePackage(customerId, pkg.packageId)
                                                                        }
                                                                    >
                                                                        <Minus size={14} />
                                                                    </button>
                                                                    <KeypadInput
                                                                        className={pkgStyles.pkgQtyInput}
                                                                        overlayClassName={styles.keypadRightOverlay}
                                                                        value={pkg.quantity}
                                                                        title="Số lượng"
                                                                        mode="integer"
                                                                        allowDecimal={false}
                                                                        onChange={(raw) => {
                                                                            const v = parseInt(raw, 10);
                                                                            if (!isNaN(v) && v > 0)
                                                                                updatePackageQuantity(
                                                                                    customerId,
                                                                                    pkg.packageId,
                                                                                    v,
                                                                                );
                                                                        }}
                                                                    />
                                                                    <button
                                                                        className={pkgStyles.pkgQtyBtn}
                                                                        onClick={() =>
                                                                            addPackageItem(customerId, pkg)
                                                                        }
                                                                    >
                                                                        <Plus size={14} />
                                                                    </button>
                                                                </div>
                                                                {canOverridePackage && (
                                                                    <button
                                                                        className={pkgStyles.pkgEditBtn}
                                                                        onClick={() =>
                                                                            setEditingPackageUid(pkg._uid)
                                                                        }
                                                                        title="Chỉnh giá/buổi/HSD"
                                                                    >
                                                                        <Pencil size={18} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* ===== Gói dịch vụ đã mua (khi sửa đơn có gói) ===== */}
                                    {existingPackages.length > 0 && (
                                        <div className={pkgStyles.existSection}>
                                            <div className={pkgStyles.existTitle}>Gói dịch vụ đã mua</div>
                                            {existingPackages.map((pkg) => {
                                                const name = pkg.templateSnapshot?.name || `Thẻ ${pkg.code}`;
                                                const urlImg = pkg.templateSnapshot?.urlImg || pkg.package?.urlImg;
                                                const used = Number(pkg.usedSessions || 0);
                                                const isLocked = used > 0;
                                                return (
                                                    <div key={pkg.id} className={pkgStyles.existRow}>
                                                        {urlImg ? (
                                                            <img
                                                                src={urlImg}
                                                                alt=""
                                                                className={pkgStyles.pkgImg}
                                                            />
                                                        ) : (
                                                            <div className={pkgStyles.pkgImgPlaceholder}>
                                                                <Package size={20} />
                                                            </div>
                                                        )}
                                                        <div className={pkgStyles.existInfo}>
                                                            <div className={pkgStyles.pkgName}>{name}</div>
                                                            <div className={pkgStyles.existPrice}>
                                                                {formatCurrency(pkg.totalContractValue)}
                                                            </div>
                                                            {isLocked ? (
                                                                <div className={pkgStyles.existLocked}>
                                                                    <Lock size={13} /> Đã dùng {used} buổi — không
                                                                    thể xoá
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    className={pkgStyles.existRemove}
                                                                    onClick={() =>
                                                                        setExistingPackages((prev) =>
                                                                            prev.filter((p) => p.id !== pkg.id),
                                                                        )
                                                                    }
                                                                >
                                                                    <Trash2 size={15} /> Xoá khỏi đơn
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* ===== Dùng gói dịch vụ (tiêu buổi từ thẻ đã mua) ===== */}
                                    {hasUseSection && (
                                        <div className={pkgStyles.useSection}>
                                            <div
                                                className={pkgStyles.useHeader}
                                                onClick={() => setShowUsePackageSection((v) => !v)}
                                            >
                                                <div className={pkgStyles.useTitleWrap}>
                                                    <span className={pkgStyles.useTitle}>Dùng gói dịch vụ</span>
                                                    {sessionCart.length > 0 && (
                                                        <span className={pkgStyles.useBadge}>
                                                            {sessionCart.reduce(
                                                                (s, x) => s + Number(x.sessionCount || 0),
                                                                0,
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                {showUsePackageSection ? (
                                                    <ChevronUp size={18} color="#0098F6" />
                                                ) : (
                                                    <ChevronDown size={18} color="#0098F6" />
                                                )}
                                            </div>

                                            {showUsePackageSection &&
                                                (loadingActivePackages ? (
                                                    <div className={pkgStyles.useEmpty}>Đang tải...</div>
                                                ) : usableActivePackages.length === 0 ? (
                                                    <div className={pkgStyles.useEmpty}>
                                                        Khách không có gói dịch vụ đang hoạt động
                                                    </div>
                                                ) : (
                                                    usableActivePackages.map((pkg) => {
                                                        const pkgName =
                                                            pkg.templateSnapshot?.name ||
                                                            pkg.package?.name ||
                                                            `Thẻ ${pkg.code}`;
                                                        const remaining = Number(pkg.remainingSessions || 0);
                                                        const amtPerSess = Number(pkg.amountPerSession || 0);
                                                        const baseUsed = Number(pkg.usedSessions || 0);
                                                        const sessInCart = sessionCart.find(
                                                            (s) => s.customerPackageId === pkg.id,
                                                        );
                                                        const isInCart = !!sessInCart;
                                                        const qty = isInCart ? Number(sessInCart.sessionCount) : 0;

                                                        const addSession = (count) =>
                                                            addSessionItem(customerId, {
                                                                customerPackageId: pkg.id,
                                                                code: pkg.code,
                                                                packageName: pkgName,
                                                                amountPerSession: amtPerSess,
                                                                remainingSessions: remaining,
                                                                sessionCount: count,
                                                            });

                                                        return (
                                                            <div
                                                                key={pkg.id}
                                                                className={`${pkgStyles.useCard}${isInCart ? ` ${pkgStyles.useCardActive}` : ''}`}
                                                            >
                                                                <div className={pkgStyles.useCardHead}>
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <div className={pkgStyles.useName}>
                                                                            {pkgName}
                                                                        </div>
                                                                        <div className={pkgStyles.useMeta}>
                                                                            {pkg.code} · Còn {remaining} buổi ·{' '}
                                                                            {formatCurrency(amtPerSess)}/buổi
                                                                        </div>
                                                                    </div>
                                                                    {isInCart && (
                                                                        <span className={pkgStyles.useChosenTag}>
                                                                            Đã chọn
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {isInCart ? (
                                                                    <>
                                                                        <div className={pkgStyles.useQtyRow}>
                                                                            <span className={pkgStyles.usePriceText}>
                                                                                {qty} buổi ·{' '}
                                                                                <span
                                                                                    className={pkgStyles.usePriceStrike}
                                                                                >
                                                                                    {formatCurrency(amtPerSess * qty)}
                                                                                </span>{' '}
                                                                                → 0đ
                                                                            </span>
                                                                            <div className={pkgStyles.useQtyControls}>
                                                                                <button
                                                                                    className={pkgStyles.useQtyBtn}
                                                                                    onClick={() => {
                                                                                        if (qty <= 1)
                                                                                            removeSessionItem(
                                                                                                customerId,
                                                                                                sessInCart._uid,
                                                                                            );
                                                                                        else addSession(qty - 1);
                                                                                    }}
                                                                                >
                                                                                    <Minus size={14} />
                                                                                </button>
                                                                                <span className={pkgStyles.useQtyNum}>
                                                                                    {qty}
                                                                                </span>
                                                                                <button
                                                                                    className={pkgStyles.useQtyBtn}
                                                                                    disabled={qty >= remaining}
                                                                                    onClick={() => addSession(qty + 1)}
                                                                                >
                                                                                    <Plus size={14} />
                                                                                </button>
                                                                                <button
                                                                                    className={pkgStyles.useRemoveBtn}
                                                                                    onClick={() =>
                                                                                        removeSessionItem(
                                                                                            customerId,
                                                                                            sessInCart._uid,
                                                                                        )
                                                                                    }
                                                                                    title="Bỏ dùng gói này"
                                                                                >
                                                                                    <X size={18} />
                                                                                </button>
                                                                            </div>
                                                                        </div>

                                                                        <div className={pkgStyles.usePerfList}>
                                                                            {Array.from({ length: qty }).map(
                                                                                (_, idx) => {
                                                                                    const perfId = (
                                                                                        sessInCart.performers || []
                                                                                    )[idx];
                                                                                    const missing = !perfId;
                                                                                    const sessionNo =
                                                                                        baseUsed + 1 + idx;
                                                                                    return (
                                                                                        <div
                                                                                            key={idx}
                                                                                            className={`${pkgStyles.usePerfRow}${missing ? ` ${pkgStyles.usePerfRowMissing}` : ''}`}
                                                                                            onClick={() =>
                                                                                                setSessionPerformerPicker(
                                                                                                    {
                                                                                                        uid: sessInCart._uid,
                                                                                                        sessionIdx: idx,
                                                                                                    },
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <span
                                                                                                className={
                                                                                                    pkgStyles.usePerfLabel
                                                                                                }
                                                                                            >
                                                                                                Buổi #{sessionNo}
                                                                                            </span>
                                                                                            {missing ? (
                                                                                                <span
                                                                                                    className={
                                                                                                        pkgStyles.usePerfMissing
                                                                                                    }
                                                                                                >
                                                                                                    Chọn NV
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span
                                                                                                    className={
                                                                                                        pkgStyles.usePerfValue
                                                                                                    }
                                                                                                >
                                                                                                    <CheckCircle2
                                                                                                        size={13}
                                                                                                        color="#22C55E"
                                                                                                    />
                                                                                                    {performerName(
                                                                                                        perfId,
                                                                                                    ) || `NV ${perfId}`}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                },
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        className={pkgStyles.useAddBtn}
                                                                        disabled={remaining === 0}
                                                                        onClick={() => addSession(1)}
                                                                    >
                                                                        <Plus size={16} />
                                                                        {remaining === 0 ? 'Hết buổi' : 'Dùng 1 buổi'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ===== RIGHT: Customer + Payment ===== */}
                <div className={styles.layoutRight}>
                    {/* Customer */}
                    <div
                        className={styles.customerBar}
                        style={editBillLocked ? { cursor: 'default' } : undefined}
                        onClick={() => {
                            if (editBillLocked) {
                                showToast(
                                    'error',
                                    'Hoá đơn chứa gói/buổi dùng, không thể đổi khách hàng',
                                    2200,
                                );
                                return;
                            }
                            setShowCustomerModal(true);
                        }}
                    >
                        <div className={styles.customerAvatar} style={{ background: getAvatarColor(customer?.name) }}>
                            {(customer?.name || '?')[0].toUpperCase()}
                        </div>
                        <div className={styles.customerInfo}>
                            <div className={styles.customerName}>{customer?.name || 'Chọn khách hàng'}</div>
                            {customer?.phone && (
                                <div className={styles.customerPhone}>{formatPhoneNumber(customer.phone)}</div>
                            )}
                            {customer && !isGuest && customer.point > 0 && (
                                <div className={styles.customerPoints}>
                                    ⭐ {Number(customer.point).toLocaleString('vi-VN')} điểm
                                </div>
                            )}
                        </div>
                        {!editBillLocked && <span className={styles.customerChangeBadge}>Đổi</span>}
                    </div>

                    {/* Payment info */}
                    <div className={styles.card}>
                        <div className={styles.cardBody}>
                            {/* Discount */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Giảm giá</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <KeypadInput
                                        className={styles.formInput}
                                        overlayClassName={styles.keypadRightOverlay}
                                        style={{ flex: 1 }}
                                        value={discountValue}
                                        title="Giảm giá"
                                        suffix={discountType === 'VND' ? 'đ' : '%'}
                                        onChange={(raw) => setDiscountValue(raw)}
                                        placeholder="0"
                                    />
                                    <div className={styles.typeToggle} style={{ width: 140 }}>
                                        <button
                                            className={`${styles.typeBtn}${discountType === '%' ? ` ${styles.typeBtnActive}` : ''}`}
                                            onClick={() => setDiscountType('%')}
                                        >
                                            %
                                        </button>
                                        <button
                                            className={`${styles.typeBtn}${discountType === 'VND' ? ` ${styles.typeBtnActive}` : ''}`}
                                            onClick={() => setDiscountType('VND')}
                                        >
                                            VNĐ
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Points */}
                            {!isGuest && customer?.point > 0 && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        Dùng điểm (có: {customer.point} điểm, 1 điểm ={' '}
                                        {redeemValuePerPoint.toLocaleString('vi-VN')}đ)
                                    </label>
                                    <KeypadInput
                                        className={styles.formInput}
                                        overlayClassName={styles.keypadRightOverlay}
                                        mode="integer"
                                        value={pointUsed || ''}
                                        title="Dùng điểm"
                                        suffix="điểm"
                                        onChange={(raw) => {
                                            const v = parseInt(raw) || 0;
                                            setPointUsed(Math.min(v, customer.point, maxPointUsable));
                                        }}
                                        placeholder="0"
                                    />
                                </div>
                            )}

                            {/* Payment method */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Phương thức</label>
                                <div className={styles.typeToggle}>
                                    <button
                                        className={`${styles.typeBtn}${paymentMethod === 'Tiền mặt' ? ` ${styles.typeBtnActive}` : ''}`}
                                        onClick={() => setPaymentMethod('Tiền mặt')}
                                    >
                                        Tiền mặt
                                    </button>
                                    <button
                                        className={`${styles.typeBtn}${paymentMethod === 'Chuyển khoản' ? ` ${styles.typeBtnActive}` : ''}`}
                                        onClick={() => setPaymentMethod('Chuyển khoản')}
                                    >
                                        Chuyển khoản
                                    </button>
                                </div>
                            </div>

                            {/* Status */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Trạng thái</label>
                                <div className={styles.typeToggle}>
                                    {['paid', 'pending', 'partial'].map((s) => (
                                        <button
                                            key={s}
                                            className={`${styles.typeBtn}${status === s ? ` ${styles.typeBtnActive}` : ''}`}
                                            onClick={() => setStatus(s)}
                                        >
                                            {s === 'paid' ? 'Đã TT' : s === 'pending' ? 'Chưa TT' : 'Một phần'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {status === 'partial' && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Số tiền trả trước</label>
                                    <KeypadInput
                                        className={styles.formInput}
                                        overlayClassName={styles.keypadRightOverlay}
                                        value={prepayment}
                                        title="Số tiền trả trước"
                                        suffix="đ"
                                        onChange={(raw) => setPrepayment(raw)}
                                        placeholder="0"
                                    />
                                </div>
                            )}

                            {/* Summary */}
                            <div className={styles.summaryDivider} />
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Tạm tính</span>
                                <span className={styles.summaryValue}>{formatCurrency(amount)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>Giảm giá</span>
                                    <span className={styles.summaryValue} style={{ color: '#EF4444' }}>
                                        -{formatCurrency(discountAmount)}
                                    </span>
                                </div>
                            )}
                            {payByPoint > 0 && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>Điểm ({pointUsed} điểm)</span>
                                    <span className={styles.summaryValue} style={{ color: '#EF4444' }}>
                                        -{formatCurrency(payByPoint)}
                                    </span>
                                </div>
                            )}
                            <div className={styles.summaryDivider} />
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel} style={{ fontSize: 14, fontWeight: 700 }}>
                                    Tổng thanh toán
                                </span>
                                <span className={styles.summaryTotal}>{formatCurrency(pay)}</span>
                            </div>
                            {status === 'partial' && (
                                <>
                                    <div className={styles.summaryRow}>
                                        <span className={styles.summaryLabel}>Trả trước</span>
                                        <span className={styles.summaryValue} style={{ color: '#22C55E' }}>
                                            {formatCurrency(parseFloat(prepayment) || 0)}
                                        </span>
                                    </div>
                                    <div className={styles.summaryRow}>
                                        <span className={styles.summaryLabel}>Nợ</span>
                                        <span
                                            className={styles.summaryValue}
                                            style={{ color: '#EF4444', fontWeight: 700 }}
                                        >
                                            {formatCurrency(Math.max(pay - (parseFloat(prepayment) || 0), 0))}
                                        </span>
                                    </div>
                                </>
                            )}
                            {pointsEarned > 0 && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>Điểm nhận được</span>
                                    <span className={styles.summaryValue} style={{ color: '#22C55E' }}>
                                        +{pointsEarned} điểm
                                    </span>
                                </div>
                            )}

                            {/* Submit button */}
                            <button className={styles.submitBtnBottom} onClick={handleSubmit} disabled={isSubmitting}>
                                <Save size={18} />
                                {isSubmitting
                                    ? 'Đang xử lý...'
                                    : editBillId
                                      ? 'Cập nhật'
                                      : status === 'paid'
                                        ? 'Thanh toán'
                                        : 'Hoàn thành'}
                            </button>

                            {/* Notes — ẩn khi Khách lẻ */}
                            {!isGuest && (
                                <>
                                    <div className={styles.summaryDivider} style={{ marginTop: 12 }} />
                                    <div className={styles.formGroup} style={{ marginTop: 12 }}>
                                        <label className={styles.formLabel}>Ghi chú</label>
                                        <textarea
                                            className={styles.formTextarea}
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            placeholder="Ghi chú cho đơn hàng..."
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Ghi chú nội bộ</label>
                                        <textarea
                                            className={styles.formTextarea}
                                            value={internalNote}
                                            onChange={(e) => setInternalNote(e.target.value)}
                                            placeholder="Ghi chú nội bộ..."
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Modal */}
            {showCustomerModal && (
                <div className={styles.sideModalOverlay} onClick={() => setShowCustomerModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Chọn khách hàng</h3>
                            <button className={styles.modalClose} onClick={() => setShowCustomerModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className={styles.modalSearch}>
                            <Search size={16} />
                            <input
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                placeholder="Tìm tên, SĐT..."
                                autoFocus
                            />
                        </div>
                        <div className={styles.modalList}>
                            {filteredCustomers.map((c) => {
                                const isActive = c.id === customerId;
                                return (
                                    <div
                                        key={c.id}
                                        className={`${styles.modalItem}${isActive ? ` ${styles.modalItemActive}` : ''}`}
                                        onClick={() => handleSelectCustomer(c)}
                                    >
                                        <div
                                            className={styles.customerAvatar}
                                            style={{
                                                background: getAvatarColor(c.name),
                                                width: 40,
                                                height: 40,
                                                fontSize: 15,
                                            }}
                                        >
                                            {(c.name || '?')[0].toUpperCase()}
                                        </div>
                                        <div className={styles.modalItemInfo}>
                                            <div className={styles.modalItemName}>{c.name}</div>
                                            {c.phone && (
                                                <div className={styles.modalItemPhone}>
                                                    {formatPhoneNumber(c.phone)}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.modalItemRight}>
                                            {c.point > 0 && c.name !== 'Khách lẻ' && (
                                                <span className={styles.modalItemPoints}>
                                                    ⭐ {Number(c.point).toLocaleString('vi-VN')} điểm
                                                </span>
                                            )}
                                            {isActive && (
                                                <div className={styles.modalItemCheck}>
                                                    <Check size={13} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Employee Modal */}
            {empModal && (
                <div className={styles.sideModalOverlay} onClick={() => setEmpModal(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h3 className={styles.modalTitle}>Chọn nhân viên hỗ trợ</h3>
                                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{empModal.name}</div>
                            </div>
                            <button className={styles.modalClose} onClick={() => setEmpModal(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        {Object.keys(empSelected).length > 0 && (
                            <div style={{ padding: '10px 16px 0' }}>
                                <button
                                    className={styles.empApplyAllBtn}
                                    onClick={() => {
                                        const supportEmployees = Object.keys(empSelected).map((id) => ({
                                            employeeId: id,
                                        }));
                                        setSupportEmployeesForAll(customerId, supportEmployees);
                                        setEmpModal(null);
                                    }}
                                >
                                    <Users size={15} /> Áp dụng cho tất cả dịch vụ
                                </button>
                            </div>
                        )}
                        <div className={styles.modalSearch}>
                            <Search size={16} />
                            <input
                                value={empSearch}
                                onChange={(e) => setEmpSearch(e.target.value)}
                                placeholder="Tìm nhân viên..."
                                autoFocus
                            />
                        </div>
                        <div className={styles.modalList}>
                            {employees
                                .filter((emp) => emp.user?.role !== 'checkin')
                                .filter((emp) => {
                                    if (!empSearch.trim()) return true;
                                    return normalize(emp.name).includes(normalize(empSearch));
                                })
                                .map((emp) => {
                                    const isSelected = !!empSelected[emp.id];
                                    return (
                                        <div
                                            key={emp.id}
                                            className={`${styles.modalItem}${isSelected ? ` ${styles.modalItemActive}` : ''}`}
                                            onClick={() =>
                                                setEmpSelected((prev) => {
                                                    const copy = { ...prev };
                                                    if (copy[emp.id]) delete copy[emp.id];
                                                    else copy[emp.id] = true;
                                                    return copy;
                                                })
                                            }
                                        >
                                            <div
                                                className={styles.customerAvatar}
                                                style={{
                                                    background: getAvatarColor(emp.name),
                                                    width: 38,
                                                    height: 38,
                                                    fontSize: 14,
                                                }}
                                            >
                                                {(emp.name || '?')[0].toUpperCase()}
                                            </div>
                                            <div className={styles.modalItemInfo}>
                                                <div className={styles.modalItemName}>{emp.name}</div>
                                                {emp.position && (
                                                    <div className={styles.modalItemPhone}>{emp.position}</div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div className={styles.modalItemCheck}>
                                                    <Check size={13} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                        <div style={{ padding: '14px 16px', borderTop: '1px solid #ECEEF2', display: 'flex', gap: 10 }}>
                            <button
                                style={{
                                    flex: 1,
                                    height: 44,
                                    border: '1.5px solid #ECEEF2',
                                    borderRadius: 10,
                                    background: '#fff',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }}
                                onClick={() => setEmpModal(null)}
                            >
                                Huỷ
                            </button>
                            <button
                                style={{
                                    flex: 2,
                                    height: 44,
                                    border: 'none',
                                    borderRadius: 10,
                                    background: '#0098F6',
                                    color: '#fff',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }}
                                onClick={() => {
                                    const supportEmployees = Object.keys(empSelected).map((id) => ({ employeeId: id }));
                                    setSupportEmployees(
                                        customerId,
                                        empModal.id,
                                        supportEmployees,
                                        empModal.selectedVariant?.id || null,
                                    );
                                    setEmpModal(null);
                                }}
                            >
                                Xác nhận ({Object.keys(empSelected).length})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal chọn NV bán gói + override gói */}
            <PackageSellerPickerModal
                open={!!sellerPickerUid}
                onClose={() => setSellerPickerUid(null)}
                customerId={customerId}
                uid={sellerPickerUid}
            />
            <PackageItemEditModal
                open={!!editingPackageUid && canOverridePackage}
                onClose={() => setEditingPackageUid(null)}
                customerId={customerId}
                uid={editingPackageUid}
            />
            <SessionPerformerPickerModal
                open={!!sessionPerformerPicker}
                onClose={() => setSessionPerformerPicker(null)}
                customerId={customerId}
                uid={sessionPerformerPicker?.uid}
                sessionIdx={sessionPerformerPicker?.sessionIdx}
            />

            {/* Toast */}
            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div
                            className={styles.toastIcon}
                            style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
